import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Lock, Heart } from "lucide-react";
import { toast } from "sonner";
import { BonusBadge } from "../lib/gameBonus";
import useLiveGames from "../lib/useLiveGames";

export default function Games() {
  const { user } = useAuth();
  const games = useLiveGames();
  const [favs, setFavs] = useState(new Set());
  const [bonuses, setBonuses] = useState({});
  const [tab, setTab] = useState("all"); // "all" | "favourites"

  useEffect(() => {
    if (!user) { setFavs(new Set()); setBonuses({}); return; }
    api.get("/me/favourites").then(({ data }) => setFavs(new Set(data.game_ids || []))).catch(() => {});
    api.get("/me/game-bonuses").then(({ data }) => setBonuses(data.bonuses || {})).catch(() => setBonuses({}));
  }, [user]);

  const toggleFav = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to save favourites");
      return;
    }
    const isFav = favs.has(id);
    // optimistic
    const next = new Set(favs);
    if (isFav) next.delete(id); else next.add(id);
    setFavs(next);
    try {
      if (isFav) await api.delete(`/me/favourites/${id}`);
      else await api.post(`/me/favourites/${id}`);
    } catch (err) {
      // rollback
      const rollback = new Set(favs);
      setFavs(rollback);
      toast.error(formatApiError(err));
    }
  };

  const filtered = tab === "favourites" ? games.filter((g) => favs.has(g.id)) : games;

  return (
    <div className="min-h-screen bg-[#030705] text-white">
      <Navbar />
      <section className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 pt-8 sm:pt-12 md:pt-20 pb-6 sm:pb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Game library</div>
        <h1 className="font-display text-3xl font-black tracking-tighter min-[360px]:text-4xl md:text-7xl">Pick your reels.</h1>

        {/* Tab switcher */}
        <div className="mt-8 md:mt-10 inline-flex max-w-full p-1 rounded-full bg-[#0A130E] border border-white/10" data-testid="library-tabs">
          <Tab active={tab === "all"} onClick={() => setTab("all")} count={games.length} testid="tab-all">
            All games
          </Tab>
          <Tab active={tab === "favourites"} onClick={() => setTab("favourites")} icon={Heart} count={favs.size} testid="tab-favourites">
            Favourites
          </Tab>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-3 sm:px-6 md:px-10 pb-32 md:pb-24 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5 sm:gap-5">
        {filtered.map((g) => {
          const playable = g.status === "live";
          const Wrapper = playable ? Link : "div";
          const props = playable ? { to: `/games/${g.id}` } : {};
          const isFav = favs.has(g.id);
          return (
            <Wrapper
              key={g.id}
              data-testid={`game-card-${g.id}`}
              {...props}
              className={`group relative min-w-0 rounded-2xl overflow-hidden border border-white/5 bg-[#0A130E] ${
                playable ? "cursor-pointer" : "opacity-70"
              }`}
            >
              <div className="relative aspect-[5/4] overflow-hidden bg-gradient-to-br from-[#0B2115] to-[#030705]">
                {g.image_url ? (
                  <img
                    src={g.image_url}
                    alt={g.name}
                    className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-700 group-hover:scale-105 sm:object-cover sm:p-0"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>

              {/* Favourite heart */}
              <button
                onClick={(e) => toggleFav(e, g.id)}
                data-testid={`fav-${g.id}`}
                title={isFav ? "Remove from favourites" : "Add to favourites"}
                className={`absolute top-2.5 left-2.5 sm:top-4 sm:left-4 z-10 p-2 rounded-full backdrop-blur border transition-all ${
                  isFav
                    ? "bg-[#D4AF37] border-[#D4AF37] text-black"
                    : "bg-black/40 border-white/20 text-white hover:bg-black/60"
                }`}
              >
                <Heart className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
              </button>

              {playable && (
                <BonusBadge game={g} bonuses={bonuses} className="absolute right-2.5 top-2.5 z-10 sm:right-4 sm:top-4" />
              )}

              {!playable && (
                <div className="absolute top-2.5 right-2.5 sm:top-4 sm:right-4 px-2 sm:px-3 py-1 rounded-full bg-black/70 backdrop-blur border border-white/10 text-[10px] sm:text-xs flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> Coming soon
                </div>
              )}
              <div className="relative p-3 sm:p-5">
                <h3 className="font-display text-base sm:text-xl font-bold leading-tight mb-1 line-clamp-1">{g.name}</h3>
                <p className="hidden sm:block text-sm text-white/60 line-clamp-2">{g.description}</p>
                {playable && (
                  <div className="mt-2 sm:mt-4 flex items-center justify-between gap-2 text-xs">
                    {Number(g.rtp) > 0 && <span className="text-white/40">RTP {g.rtp}%</span>}
                    <span className="text-[#D4AF37] font-semibold whitespace-nowrap">Play -&gt;</span>
                  </div>
                )}
              </div>
            </Wrapper>
          );
        })}

        {filtered.length === 0 && tab === "favourites" && (
          <div className="col-span-full text-center py-24" data-testid="empty-favourites">
            <Heart className="w-10 h-10 text-white/20 mx-auto mb-4" />
            <p className="font-display text-2xl font-bold mb-2">No favourites yet</p>
            <p className="text-sm text-white/50 mb-6">
              Tap the heart on any game to save it here for quick access.
            </p>
            <button
              onClick={() => setTab("all")}
              className="px-6 py-2.5 rounded-full bg-[#D4AF37] text-black font-semibold hover:bg-[#F1C40F]"
            >
              Browse all games
            </button>
          </div>
        )}
        {filtered.length === 0 && tab === "all" && (
          <div className="col-span-full text-center text-white/40 py-20">No games yet.</div>
        )}
      </section>
      <Footer />
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, children, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
        active
          ? "bg-[#D4AF37] text-black"
          : "text-white/70 hover:text-white"
      }`}
    >
      {Icon && <Icon className={`w-4 h-4 ${active && Icon === Heart ? "fill-current" : ""}`} />}
      {children}
      <span
        className={`ml-1 px-2 py-0.5 rounded-full text-[10px] tabular-nums ${
          active ? "bg-black/15 text-black" : "bg-white/10 text-white/60"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
